import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import type { ClientFeature } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { DateLisible } from '../components/Nombre'
import { Alert, Badge, Button, Card, DataTable, FormActions, Grid, Stack, TextField, useToast } from '../components/ui'
import type { Column } from '../components/ui'

/** L'exception qu'on s'apprête à poser : accorder ou retirer, et à quelle fonctionnalité. */
interface Saisie {
  code: string
  included: boolean
}

/**
 * Les fonctionnalités d'un client : ce que sa formule donne, et les exceptions qui la
 * corrigent.
 *
 * TROIS COLONNES ET NON UNE, parce qu'une seule mentirait. « Formule » dit ce que le client
 * paie ; « Exception » dit ce qu'on a décidé pour lui, avec son motif et sa fenêtre ;
 * « Aujourd'hui » dit ce qu'il a réellement — la réponse de la même règle que celle qui
 * refuse les routes au client. Un client Essentiel qui a les ventes par exception doit se
 * lire comme tel, et non comme un client qui aurait acheté les ventes.
 *
 * UNE EXCEPTION ÉCHUE RESTE AFFICHÉE, marquée comme telle. Elle ne décide plus rien, mais
 * c'est elle qui explique au super-administrateur pourquoi un écran vient de disparaître
 * chez ce client.
 *
 * LE MOTIF EST EXIGÉ AVANT L'ENVOI, dans les deux sens : la base le refuserait de toute
 * façon (`platform.reason_required`), mais le bouton reste inactif tant qu'il manque, pour
 * ne pas faire cliquer pour rien.
 */
export function ClientFeaturesCard({ organizationId }: { organizationId: number }) {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()

  const token = session?.token ?? ''

  const [lignes, setLignes] = useState<ClientFeature[]>([])
  const [chargement, setChargement] = useState(true)
  const [saisie, setSaisie] = useState<Saisie | null>(null)
  const [motif, setMotif] = useState('')
  const [echeance, setEcheance] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return

    setChargement(true)

    api
      .listPlatformClientFeatures(token, organizationId)
      .then(setLignes)
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
      .finally(() => setChargement(false))
  }, [token, organizationId, notify, describeError])

  useEffect(charger, [charger])

  const envoyer = async (code: string, included: boolean | null) => {
    setEnvoi(true)

    try {
      await api.setPlatformClientFeature(token, organizationId, code, {
        included,
        reason: included === null ? null : motif.trim(),
        expiresOn: included === null || echeance === '' ? null : echeance,
      })
      notify(t(included === null ? 'platform.client.features.cleared' : 'platform.client.features.saved'), 'succes')
      setSaisie(null)
      setMotif('')
      setEcheance('')
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const colonnes: Column<ClientFeature>[] = [
    { key: 'feature', header: t('platform.client.features.feature'), render: (f) => t(`feature.${f.code}` as never) },
    {
      key: 'plan',
      header: t('platform.client.features.plan'),
      render: (f) => (f.inPlan ? t('platform.client.features.included') : t('platform.client.features.notIncluded')),
    },
    {
      key: 'override',
      header: t('platform.client.features.override'),
      render: (f) =>
        f.overrideIncluded === null ? (
          <span className="text-texte-doux">—</span>
        ) : (
          <span className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <Badge tone={f.overrideIncluded ? 'succes' : 'avertissement'}>
                {t(f.overrideIncluded ? 'platform.client.features.granted' : 'platform.client.features.withdrawn')}
              </Badge>
              {f.source === 'plan' && <Badge tone="info">{t('platform.client.features.inactive')}</Badge>}
            </span>
            <span className="text-[0.8rem] text-texte-doux">{f.overrideReason}</span>
            {f.overrideExpiresOn !== null && (
              <span className="text-[0.8rem] text-texte-doux">
                {t('platform.client.features.until')} <DateLisible valeur={f.overrideExpiresOn} />
              </span>
            )}
          </span>
        ),
    },
    {
      key: 'today',
      header: t('platform.client.features.today'),
      render: (f) => (
        <Badge tone={f.included ? 'succes' : 'avertissement'}>
          {t(f.included ? 'platform.client.features.on' : 'platform.client.features.off')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (f) => (
        <span className="flex flex-wrap gap-2">
          <Button variant="secondaire" onClick={() => setSaisie({ code: f.code, included: !f.included })}>
            {t(f.included ? 'platform.client.features.withdraw' : 'platform.client.features.grant')}
          </Button>
          {f.overrideIncluded !== null && (
            <Button variant="discret" onClick={() => void envoyer(f.code, null)} disabled={envoi}>
              {t('platform.client.features.clear')}
            </Button>
          )}
        </span>
      ),
    },
  ]

  return (
    <Card title={t('platform.client.features')}>
      <Stack gap={3}>
        <p className="m-0 text-[0.85rem] text-texte-doux">{t('platform.client.features.explain')}</p>

        {chargement ? (
          <p className="text-texte-doux">{t('common.loading')}</p>
        ) : (
          <DataTable columns={colonnes} rows={lignes} rowKey={(f) => f.code} emptyLabel={t('platform.client.features.empty')} />
        )}

        {saisie !== null && (
          <Stack gap={3}>
            <Alert tone={saisie.included ? 'info' : 'avertissement'}>
              {t(saisie.included ? 'platform.client.features.grantExplain' : 'platform.client.features.withdrawExplain')}{' '}
              <b>{t(`feature.${saisie.code}` as never)}</b>
            </Alert>
            <Grid min="15rem">
              <TextField label={t('platform.client.reason')} value={motif} onChange={setMotif} required />
              <TextField
                label={t('platform.client.features.expiresOn')}
                value={echeance}
                onChange={setEcheance}
                type="date"
                hint={t('platform.client.features.expiresHint')}
                ltr
              />
            </Grid>
            <FormActions>
              <Button
                onClick={() => void envoyer(saisie.code, saisie.included)}
                disabled={motif.trim() === ''}
                pending={envoi}
                pendingLabel={t('common.loading')}
              >
                {t(saisie.included ? 'platform.client.features.grant' : 'platform.client.features.withdraw')}
              </Button>
              <Button variant="secondaire" onClick={() => setSaisie(null)} disabled={envoi}>
                {t('common.cancel')}
              </Button>
            </FormActions>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
